/**
 * Token Manager
 * 
 * Centralized JWT token storage and management.
 * Handles secure storage of access and refresh tokens in localStorage.
 */

const TOKEN_KEY = 'dbutler_access_token';
const REFRESH_TOKEN_KEY = 'dbutler_refresh_token';
const USER_KEY = 'dbutler_user';

/**
 * Save authentication tokens to localStorage
 * @param {Object} tokens - Object containing accessToken and refreshToken
 * @param {string} tokens.accessToken - JWT access token
 * @param {string} tokens.refreshToken - JWT refresh token
 */
export const saveTokens = (tokens) => {
  if (!tokens || !tokens.accessToken || !tokens.refreshToken) {
    console.error('Invalid tokens object provided to saveTokens');
    return;
  }
  
  try {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  } catch (error) {
    console.error('Error saving tokens to localStorage:', error);
  }
};

/**
 * Get the access token from localStorage
 * @returns {string|null} The access token or null if not found
 */
export const getAccessToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving access token:', error);
    return null;
  }
};

/**
 * Get the refresh token from localStorage
 * @returns {string|null} The refresh token or null if not found
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

/**
 * Save user data to localStorage
 * @param {Object} user - User object from authentication response
 */
export const saveUser = (user) => {
  if (!user) {
    console.error('Invalid user object provided to saveUser');
    return;
  }
  
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Error saving user to localStorage:', error);
  }
};

/**
 * Get user data from localStorage
 * @returns {Object|null} The user object or null if not found
 */
export const getUser = () => {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error retrieving user from localStorage:', error);
    return null;
  }
};

/**
 * Clear all authentication data from localStorage
 * Used during logout or when tokens are invalid
 */
export const clearTokens = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Error clearing tokens from localStorage:', error);
  }
};

/**
 * Check if user is authenticated (has valid tokens)
 * @returns {boolean} True if tokens exist, false otherwise
 */
export const isAuthenticated = () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  return !!(accessToken && refreshToken);
};

/**
 * Decode JWT token to get payload
 * Note: This does NOT validate the token, only decodes it
 * @param {string} token - JWT token to decode
 * @returns {Object|null} Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if a token is expired
 * @param {string} token - JWT token to check
 * @returns {boolean} True if token is expired, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  // Add 30 second buffer to refresh before actual expiration
  return currentTime >= (expirationTime - 30000);
};

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  
  return new Date(decoded.exp * 1000);
};

/**
 * Get user role from access token
 * @returns {string|null} User role or null if not found
 */
export const getUserRole = () => {
  const token = getAccessToken();
  if (!token) return null;
  
  const decoded = decodeToken(token);
  return decoded?.role || null;
};

/**
 * Get merchant ID from access token
 * @returns {number|null} Merchant ID or null if not found
 */
export const getMerchantId = () => {
  const token = getAccessToken();
  if (!token) return null;
  
  const decoded = decodeToken(token);
  return decoded?.merchantId || null;
};

/**
 * Check if access token needs refresh
 * Returns true if token is expired or will expire soon
 * @returns {boolean}
 */
export const needsTokenRefresh = () => {
  const accessToken = getAccessToken();
  return isTokenExpired(accessToken);
};

export default {
  saveTokens,
  getAccessToken,
  getRefreshToken,
  saveUser,
  getUser,
  clearTokens,
  isAuthenticated,
  decodeToken,
  isTokenExpired,
  getTokenExpiration,
  getUserRole,
  getMerchantId,
  needsTokenRefresh,
};
